/**
 * Result 画面: phase==='finished' のときに Play の代わりに表示される結果ページ。
 *
 * - 勝者バナー (your view: 勝ち / 負け / 引き分け)
 * - 盤ごとの結果カード一覧 (アコーディオン)。 サムネ + 結果バッジが常時表示、
 *   クリックで展開すると大盤 + 再生コントロール + 棋譜 (折りたたみ) が出る。
 * - 「もう一局」 / 「ホームへ」 / SGF DL は menu 経由
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { BoardSideLabel } from '../components/BoardSideLabel';
import { BoardRenderer } from '../components/board/BoardRenderer';
import { BridgeScoreComparison } from '../components/board/BridgeScoreComparison';
import { PokerInlineCard } from '../components/board/PokerCard';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { Loading } from '../components/Loading';
import { TitleBar } from '../components/TitleBar';
import { WasmError } from '../components/WasmError';
import { type BridgeNtMove, type BridgeNtState, bridgeSuitColor } from '../games/bridge-nt';
import type { PokerMatchState } from '../games/heads-up-poker';
import { rankLabel, suitLabel } from '../games/heads-up-poker';
import { bridgePublicReplay } from '../lib/bridge-public-replay';
import { navigateToRoom } from '../lib/code';
import { gameIconSlug, gameLabel, gameShort, mySideDisplay, NEXT_SEVEN_GAME_ORDER } from '../lib/game-meta';
import {
  type BoardEntry,
  type MatchSeat,
  type MoveLogEntry,
  mySeatOf,
  type Snapshot,
  sideOnBoard,
  sideToRole,
  slotSideOnBoard,
} from '../lib/match-snapshot';
import { navigate } from '../lib/navigate';
import { newGameMoveLabel } from '../lib/new-game-move-label';
import { pokerReplayNeedsSnapshot, pokerReplaySnapshot } from '../lib/poker-public-replay';
import { executeTurnstile } from '../lib/turnstile';
import { useWasmReady } from '../lib/useWasmReady';
import type { Participant } from '../proto';
import { useRoom } from '../state/room';
import { StudyButton } from '../components/StudyButton';
import { useSession } from '../state/session';
import { type GameId, getEngine } from '../wasm';

const REPLAY_STEP_MS = 700;

export function Result() {
  const snapshot = useRoom((s) => s.snapshot) as Snapshot | null;
  const you = useRoom((s) => s.you);
  const me = useSession((s) => s.me);
  const participants = useRoom((s) => s.participants);
  const outcome = useRoom((s) => s.outcome);
  const archiveKey = useRoom((s) => s.archiveKey);
  const mySeat = useMemo(() => mySeatOf(snapshot, you), [snapshot, you]);
  /** board ごとの自分の Side (per-board)。 Result では Renju swap parity を考慮しても良いが、
   *  既存挙動と一致させるため first_seat だけで判定 (state 取得時の Renju 表示と一致する)。 */
  // **盤描画用**: 物理色 (Renju Swap で反転後)。 自分の駒色を盤に反映させるための値。
  const yourSideOn = (entry: BoardEntry): 'First' | 'Second' | null =>
    sideOnBoard(snapshot, mySeat, entry.board);
  // **勝敗判定用**: slot Side (Renju Swap でも first_seat 由来で不変)。
  // `entry.result` (= renju_to_match で物理→slot 済み) と直接比較できる。
  const yourSlotOn = (entry: BoardEntry): 'First' | 'Second' | null =>
    slotSideOnBoard(snapshot, mySeat, entry.board);
  // server (mixed-do::format_outcome) は MatchOutcome を "host"/"guest"/"draw" の
  // 小文字 Role 文字列で送る。 ここを 'First'/'Host'/'Draw' (大文字) で比較していると
  // 常に false → winnerName=null + 生 outcome 文字列が表示される (全ゲームのリザルトが壊れる)。
  const winnerName = (() => {
    if (!outcome || outcome === 'draw') return null;
    const role: 'host' | 'guest' = outcome === 'host' ? 'host' : 'guest';
    return participants.find((p) => p.role === role)?.display_name ?? null;
  })();

  const matchId = archiveKey ? extractMatchId(archiveKey) : null;
  const sgfHref = matchId ? `/api/matches/${matchId}/sgf` : null;
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const { wasmReady, wasmError, retryWasm } = useWasmReady();
  /** 相手が先に再戦部屋を作成済みか。 立っていればボタンは「再戦に同意する」 に切替。 */
  const rematchCode = useRoom((s) => s.rematchCode);
  /** CPU (ローカル) 対局かどうか。 true なら再戦はオンライン部屋を立てず、 同設定の新規
   *  ローカル対局へ遷移する。 */
  const isLocal = useRoom((s) => s.isLocal);
  /** ローカル対局時は code = /local/:id の id。 setup を localStorage から引くのに使う。 */
  const roomCode = useRoom((s) => s.code);


  /** 「再戦する」 (= 自分が新部屋を作る): POST /api/rooms → 元部屋 WS に Rematch 通知 → 自分は新部屋へ移動。 */
  async function createRematch() {
    setCreating(true);
    setCreateError(null);
    try {
      const turnstileToken = await executeTurnstile();
      const res = await fetch('/api/rooms', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          'cf-turnstile-response': turnstileToken,
        },
        body: JSON.stringify({ rematch_of: roomCode ?? undefined }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { code } = (await res.json()) as { code: string };
      // 元部屋に通知 → 相手のボタンが「再戦に同意する」 に切替。 navigate より先に送る
      // (= 自分の WS が切れる前に届くように)。
      useRoom.getState().offerRematch(code);
      navigateToRoom(code);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : '作成失敗');
      setCreating(false);
    }
  }

  /** 「再戦に同意する」 (= 相手の新部屋へ join): navigate するだけ。 */
  function acceptRematch() {
    if (!rematchCode) return;
    navigateToRoom(rematchCode);
  }

  /** CPU 再戦: 直前と同じ setup (構成 / 強さ / 先後) で新しいローカル対局を立て、 /local/:id へ。
   *  setup が見つからない (= 直リンク等) ときは CPU 設定画面へフォールバック。 */
  function rematchLocal() {
    const prev = roomCode ? localStorage.getItem(`mixed-game.local.setup.${roomCode}`) : null;
    if (!prev) {
      navigate('/local/new');
      return;
    }
    const newId = generateLocalId();
    localStorage.setItem(`mixed-game.local.setup.${newId}`, prev);
    navigate(`/local/${newId}`);
  }

  // resultKind は **server outcome** ベースで判定する。
  // 自分側の board ごとの勝敗内訳 (= mine/opp/drawCount) は別途集計し、 大きい字の tally に使う。
  //
  // 重要: server outcome は WinFormula (FirstBoard / Majority / AllBoards) を反映した
  // 「マッチ最終勝敗」 で、 単純な board 勝ち数集計とは別物。 board 集計だけで判定すると、
  // 例えば FirstBoard ルールで「最初に 1 盤取られたが他で 2 盤取り返した」 状況で
  // resultKind と server outcome が乖離する。 server outcome を主、 集計を内訳として扱う。
  let resultKind: 'win' | 'loss' | 'draw' | 'unknown' = 'unknown';
  let resultLabel = '?';
  if (outcome === 'draw') {
    resultKind = 'draw';
    resultLabel = '引き分け';
  } else if (outcome === 'host' || outcome === 'guest') {
    if (you === 'host' || you === 'guest') {
      if (you === outcome) {
        resultKind = 'win';
        resultLabel = '勝ち';
      } else {
        resultKind = 'loss';
        resultLabel = '負け';
      }
    } else {
      resultKind = 'unknown';
      resultLabel = winnerName ? `${winnerName} の勝ち` : `${outcome} の勝ち`;
    }
  }

  // tally: 自分視点の 勝/負/引 数。 中断 (r==null) はカウントしない。
  let mine = 0;
  let opp = 0;
  let drawCount = 0;
  if (you === 'host' || you === 'guest') {
    const boards = snapshot?.boards ?? [];
    for (let i = 0; i < boards.length; i++) {
      const b = boards[i];
      const r = b.result;
      if (r === null) continue;
      if (r === 'Draw') {
        drawCount++;
        continue;
      }
      // 勝敗判定は **slot ベース** (b.result が slot Side のため)。 物理色 (yourSideOn) と
      // 比較すると Renju Swap 奇数回の盤で勝/負がひっくり返るバグになる。
      const mySlot = yourSlotOn(b);
      if (!mySlot) continue;
      if (r === mySlot) mine++;
      else opp++;
    }
  }
  // 2 盤以上のときに大きく出す勝敗内訳。 中断 (r==null) は含めない。
  const totalBoards = snapshot?.boards?.length ?? 0;
  const showTally = resultKind !== 'unknown' && totalBoards >= 2;

  return (
    <main className="result-shell">
      <TitleBar
        title="対局結果"
        back={{ href: '/' }}
        center={<ConnectionBadge />}
        menu={[
          ...(sgfHref
            ? [
                {
                  label: 'SGF をダウンロード',
                  onClick: () => (window.location.href = sgfHref),
                },
              ]
            : []),
          { label: '設定', onClick: () => navigate('/settings') },
          { label: 'ホームに戻る', onClick: () => navigate('/') },
        ]}
      />

      {wasmError && (
        <div className="wasm-error-banner">
          <WasmError onRetry={retryWasm} />
        </div>
      )}

      <ResultHero
        resultKind={resultKind}
        resultLabel={resultLabel}
        tally={showTally ? { mine, opp, drawCount } : null}
        opponentName={(() => {
          const oppRole = you === 'host' ? 'guest' : you === 'guest' ? 'host' : null;
          const name = oppRole ? participants.find((p) => p.role === oppRole)?.display_name : null;
          return name ?? (isLocal ? 'CPU' : null);
        })()}
        testId="finished"
      />

      <ResultBoardNav
        snapshot={snapshot}
        mySeat={mySeat}
      />

      <ResultBoardList
        snapshot={snapshot}
        participants={participants}
        mySeat={mySeat}
        sideForBoard={(b) => yourSideOn(b)}
        wasmReady={wasmReady}
      />

      <section className="home-actions">
        <StudyButton
          snapshot={snapshot}
          title={snapshot?.boards.map((b) => gameShort(b.game_id)).join(' · ') ?? ''}
          outcome={outcome ?? null}
          seat={mySeat}
          players={['host', 'guest'].map((role) =>
            participants.find((p) => p.role === role)?.display_name ?? (isLocal ? 'CPU' : 'ゲスト'))}
        />
        {isLocal ? (
          <button
            className="btn-primary"
            onClick={rematchLocal}
            data-testid="rematch"
          >
            もう一局
          </button>
        ) : rematchCode ? (
          <button
            className="btn-primary"
            onClick={acceptRematch}
            data-testid="rematch-accept"
          >
            再戦に同意する
          </button>
        ) : me ? (
          <button
            className="btn-primary"
            onClick={createRematch}
            disabled={creating}
            data-testid="rematch"
          >
            {creating ? '部屋を作成中…' : '再戦する'}
          </button>
        ) : (
          <>
            <button
              className="btn-primary"
              disabled
              data-testid="rematch"
              aria-describedby="rematch-login-required"
            >
              再戦する
            </button>
            <p id="rematch-login-required" className="muted" data-testid="rematch-login-required">
              部屋を立てるにはログインが必要です
            </p>
          </>
        )}
        <a className="btn-link" href="/">
          ホームに戻る
        </a>
        {createError && <p className="error">{createError}</p>}
      </section>
    </main>
  );
}

// ─────────────────── Shared result components ───────────────────

export type ResultKind = 'win' | 'loss' | 'draw' | 'unknown';

export function ResultHero({
  resultKind,
  resultLabel,
  tally,
  opponentName,
  testId,
}: {
  resultKind: ResultKind;
  resultLabel: string;
  tally: { mine: number; opp: number; drawCount: number } | null;
  opponentName?: string | null;
  testId?: string;
}) {
  return (
    <section className={`result-hero result-hero--${resultKind}`} data-testid={testId}>
      <strong className="result-hero-label">{resultLabel}</strong>
      {tally && (
        <strong className="result-hero-tally" data-testid="result-tally">
          {tally.mine}勝 {tally.opp}敗{tally.drawCount > 0 ? ` ${tally.drawCount}分` : ''}
        </strong>
      )}
      {opponentName && (
        <span className="result-hero-sub">vs {opponentName}</span>
      )}
    </section>
  );
}

export function ResultBoardNav({
  snapshot,
  mySeat,
}: {
  snapshot: Snapshot | null;
  mySeat: MatchSeat | null;
}) {
  const boards = snapshot?.boards;
  if (!boards || boards.length === 0) return null;
  return (
    <nav className="result-board-nav" aria-label="盤へジャンプ">
      {boards.map((b, i) => {
        const r = b.result;
        const mySlotB = slotSideOnBoard(snapshot, mySeat, b.board);
        const kind =
          r === null
            ? 'unknown'
            : r === 'Draw'
              ? 'draw'
              : mySlotB != null && r === mySlotB
                ? 'win'
                : mySlotB != null
                  ? 'loss'
                  : 'unknown';
        return (
          <a
            key={b.board}
            href={`#board-${b.board}`}
            className={`result-board-nav-item result-board-nav-item--${kind}`}
            aria-label={`${i + 1}盤目 ${gameLabel(b.game_id)} の結果へ`}
            title={`${i + 1}盤目: ${gameLabel(b.game_id)}`}
          >
            <img src={`/icons/games/${gameIconSlug(b.game_id)}.svg`} width={40} height={40} alt="" />
          </a>
        );
      })}
    </nav>
  );
}

export function ResultBoardList({
  snapshot,
  participants = [],
  mySeat,
  sideForBoard,
  wasmReady,
}: {
  snapshot: Snapshot | null;
  participants?: Participant[];
  mySeat: MatchSeat | null;
  sideForBoard: (entry: BoardEntry) => 'First' | 'Second' | null;
  wasmReady: boolean;
}) {
  const boards = snapshot?.boards;
  if (!boards || boards.length === 0) return null;
  return (
    <section>
      <h3>盤ごとの結果</h3>
      <div className="result-boards">
        {boards.map((b) => (
          <BoardResultCard
            key={b.board}
            entry={b}
            snapshot={snapshot!}
            participants={participants}
            mySide={sideForBoard(b)}
            mySeat={mySeat}
            wasmReady={wasmReady}
          />
        ))}
      </div>
    </section>
  );
}

// ─────────────────── Board card (accordion) ───────────────────

export function BoardResultCard({
  entry,
  snapshot,
  participants,
  mySide,
  mySeat,
  wasmReady,
}: {
  entry: BoardEntry;
  snapshot: Snapshot;
  participants: Participant[];
  /** この board における自分の Side (= 物理色、 Renju Swap 後は反転後)。 盤描画用。 */
  mySide: 'First' | 'Second' | null;
  /** 自分の MatchSeat (= 役割: Leader / Follower)。 観戦者は null。 勝敗判定用。 */
  mySeat: MatchSeat | null;
  wasmReady: boolean;
}) {
  const [open, setOpen] = useState(false);
  const r = entry.result;
  // 勝敗判定は **slot ベース** で行う:
  // `entry.result` は slot Side (= renju_to_match で物理 → slot 変換済み) なので、
  // 同じく slot ベースの `slotSideOnBoard` と比較する。
  // `mySide` (= 物理色) を使うと Renju Swap で逆転 (= 旧バグ、 ユーザー指摘済み)。
  const mySlotSide = slotSideOnBoard(snapshot, mySeat, entry.board);
  const userWon =
    mySlotSide != null && r != null && r !== 'Draw' && r === mySlotSide;
  const kind = r === null ? 'unknown' : r === 'Draw' ? 'draw' : userWon ? 'win' : 'loss';
  const label =
    r === null ? '中断' : r === 'Draw' ? '引き分け' : userWon ? '勝ち' : '負け';
  // 勝者 Side → Role → 参加者名。 Side だけだと「先手の勝ち」 になり誰が勝ったか分からない
  // (= UX 上の問題)。 sideToRole で host/guest を解決し participants を引く。
  const winnerName =
    r === 'First' || r === 'Second'
      ? (() => {
          const role = sideToRole(snapshot, entry.board, r);
          if (!role) return null;
          return participants.find((p) => p.role === role)?.display_name ?? null;
        })()
      : null;

  return (
    <details
      id={`board-${entry.board}`}
      className={`board-result-card board-result-card--${kind} ${open ? 'is-open' : ''}`}
      data-testid="board-card"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="board-result-summary" data-testid="board-card-toggle">
        <div className="board-result-summary-inner">
          <header>
            <span className="board-result-game">{gameShort(entry.game_id)}</span>
            {NEXT_SEVEN_GAME_ORDER.includes(entry.game_id) ? <span className="board-result-side-label"><BoardSideLabel entry={entry} yourSide={mySide}/></span> : (() => {
              const swapped = entry.game_id === 'Renju' && ((entry.state as { swap_count?: number } | null)?.swap_count ?? 0) % 2 === 1;
              const sd = mySide ? mySideDisplay(entry.game_id, mySide, swapped) : null;
              return sd && <span className="board-result-side-label">{sd.side}{sd.icon && <span className="board-result-piece">{sd.icon}</span>}{sd.swap ? `(${sd.swap})` : ''}</span>;
            })()}
            <span className="board-result-tag" data-result={kind}>
              {label}
            </span>
          </header>
          {entry.game_id === 'BridgeNT' && <BridgeScoreComparison state={entry.state as BridgeNtState} yourSide={mySide}/>}
          <div className="board-result-thumb">
            <BoardRenderer entry={entry} yourSide={mySide} mini />
          </div>
          <div className="board-result-footer">
            <span>
              {entry.history.length}手
              {r === 'Draw' ? ' · 引き分け' : winnerName ? ` · 勝者 ${winnerName}` : r ? ` · 勝者 ${r}` : ''}
            </span>
            <span className="board-result-chevron" aria-hidden>
              {open ? '▴' : '▾'}
            </span>
          </div>
        </div>
      </summary>

      {open && (
        <div className="board-result-replay" data-testid="board-replay">
          <BoardReplay entry={entry} yourSide={mySide} wasmReady={wasmReady} />
        </div>
      )}
    </details>
  );
}

// ─────────────────── Replay (per-board) ───────────────────

function BoardReplay({
  entry,
  yourSide,
  wasmReady,
}: {
  entry: BoardEntry;
  yourSide: 'First' | 'Second' | null;
  wasmReady: boolean;
}) {
  const totalPlies = entry.history.length;
  const snapshotOnly = entry.game_id === 'HeadsUpPoker' && pokerReplayNeedsSnapshot(entry.history);
  const [currentPly, setCurrentPly] = useState(totalPlies);
  const [playing, setPlaying] = useState(false);

  // 自動再生
  useEffect(() => {
    if (!playing) return;
    if (currentPly >= totalPlies) {
      setPlaying(false);
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPly((p) => Math.min(p + 1, totalPlies));
    }, REPLAY_STEP_MS);
    return () => clearTimeout(timer);
  }, [playing, currentPly, totalPlies]);

  const replayState = useReplayState(entry.game_id, entry.history, currentPly, wasmReady, entry.state);
  const lastMv = currentPly > 0 ? entry.history[currentPly - 1] : null;
  // Connect6 の 1 ターン 2 石ハイライト用 (他ゲームは未使用)。
  const prevMv = currentPly > 1 ? entry.history[currentPly - 2] : null;

  return (
    <>
      <div className="replay-board">
        {replayState != null ? (
          <BoardRenderer
            entry={{ ...entry, state: replayState }}
            yourSide={yourSide}
            legal={undefined}
            myTurnAllowed={false}
            onMove={() => {}}
            lastMove={lastMv?.mv}
            prevMove={prevMv?.mv}
            staticView
          />
        ) : (
          <Loading label="局面を読み込み中" />
        )}
      </div>

      {snapshotOnly ? (
        <p className="muted">終了時の局面と公開された手札を表示しています。</p>
      ) : <div className="replay-controls">
        <button
          className="btn-icon"
          onClick={() => {
            setPlaying(false);
            setCurrentPly(0);
          }}
          disabled={currentPly === 0}
          aria-label="最初へ"
          data-testid="replay-first"
        >
          ⏮
        </button>
        <button
          className="btn-icon"
          onClick={() => {
            setPlaying(false);
            setCurrentPly((p) => Math.max(0, p - 1));
          }}
          disabled={currentPly === 0}
          aria-label="前へ"
          data-testid="replay-prev"
        >
          ◀
        </button>
        <button
          className="btn-icon btn-icon--play"
          onClick={() => {
            if (currentPly >= totalPlies) setCurrentPly(0);
            setPlaying((p) => !p);
          }}
          aria-label={playing ? '停止' : '再生'}
          data-testid="replay-play"
        >
          {playing ? '⏸' : '▶'}
        </button>
        <button
          className="btn-icon"
          onClick={() => {
            setPlaying(false);
            setCurrentPly((p) => Math.min(totalPlies, p + 1));
          }}
          disabled={currentPly >= totalPlies}
          aria-label="次へ"
          data-testid="replay-next"
        >
          ▶
        </button>
        <button
          className="btn-icon"
          onClick={() => {
            setPlaying(false);
            setCurrentPly(totalPlies);
          }}
          disabled={currentPly >= totalPlies}
          aria-label="最後へ"
          data-testid="replay-last"
        >
          ⏭
        </button>
        <input
          type="range"
          min={0}
          max={totalPlies}
          value={currentPly}
          onChange={(e) => {
            setPlaying(false);
            setCurrentPly(Number(e.target.value));
          }}
          className="replay-slider"
          aria-label="手数"
          data-testid="replay-slider"
        />
        <span className="replay-ply mono">
          {currentPly}/{totalPlies}
        </span>
      </div>}

      {lastMv && (
        <div className="replay-last-move muted">
          <span className="kifu-side" data-side={lastMv.side}>
            {lastMv.side === 'First' ? '先' : '後'}
          </span>
          <span className="kifu-mv">{describeMove(entry.game_id, lastMv.mv, lastMv.side)}</span>
        </div>
      )}

      <details className="kifu-details" data-testid="kifu-details">
        <summary>棋譜 ({totalPlies}手)</summary>
        <KifuList
          entry={entry}
          currentPly={currentPly}
          onJump={(p) => {
            if (snapshotOnly) return;
            setPlaying(false);
            setCurrentPly(p);
          }}
        />
      </details>
    </>
  );
}

// ─────────────────── Replay state computation ───────────────────

/**
 * `history[0..currentPly]` を init から apply 連鎖して中間 state を返す。 wasm 初期化前は null。
 * Backgammon は dice が state に必要なので、 各 mv を apply する直前に必要な dice
 * 値を `state.dice` に補充する。
 * 未完了 Bridge の公開棋譜は配牌を含まないため、公開された出札だけから復元する。
 */
function useReplayState(
  gameId: GameId,
  history: MoveLogEntry[],
  upToPly: number,
  wasmReady: boolean,
  finalState: unknown,
): unknown | null {
  return useMemo(() => {
    // Redacted Poker provides cannot be replayed by the authoritative engine:
    // its missing-card showdown fallback would manufacture a different winner.
    if (gameId === 'HeadsUpPoker' && pokerReplayNeedsSnapshot(history)) {
      return pokerReplaySnapshot(finalState as PokerMatchState);
    }
    if (!wasmReady) return null;
    try {
      if (gameId === 'BridgeNT' && (finalState as BridgeNtState).phase !== 'complete') {
        return bridgePublicReplay(finalState as BridgeNtState, history, upToPly);
      }
      const api = getEngine(gameId);
      let state = api.init();
      for (let i = 0; i < upToPly && i < history.length; i++) {
        const mv = history[i].mv;
        if (gameId === 'Backgammon') {
          state = bgEnsureDice(state, mv);
        }
        state = api.apply(state, mv);
      }
      return state;
    } catch (e) {
      console.warn('replay apply failed', e);
      return null;
    }
  }, [gameId, history, upToPly, wasmReady, finalState]);
}

/** Backgammon の Move から dice を取り出して、 state.dice に無ければ追加。 */
function bgEnsureDice(state: unknown, mv: unknown): unknown {
  let dice: number | null = null;
  if (typeof mv === 'object' && mv !== null) {
    const m = mv as {
      step?: { dice: number };
      enter?: { dice: number };
      bear_off?: { dice: number };
    };
    dice = m.step?.dice ?? m.enter?.dice ?? m.bear_off?.dice ?? null;
  }
  if (dice == null) return state;
  const s = state as { dice?: number[] } & Record<string, unknown>;
  const cur = s.dice ?? [];
  if (cur.includes(dice)) return state;
  return { ...s, dice: [...cur, dice] };
}

// ─────────────────── Helpers ───────────────────

function KifuList({
  entry,
  currentPly,
  onJump,
}: {
  entry: BoardEntry;
  currentPly: number;
  onJump: (ply: number) => void;
}) {
  const listRef = useRef<HTMLOListElement | null>(null);
  // 現在 ply に追従してスクロール。 currentPly は dep の sentinel (DOM 側の data-active を
  // 起点に query するので body 中で直接読まないが、 ply 変化のタイミングで fire させたい)。
  // biome-ignore lint/correctness/useExhaustiveDependencies: currentPly は scroll trigger の sentinel
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector<HTMLElement>('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentPly]);

  if (entry.history.length === 0) {
    return <p className="muted">指し手なし</p>;
  }
  const rows = pairPlies(entry.history);
  const renderCell = (slot: KifuSlot) => {
    if (!slot) return <span className="kifu-cell kifu-cell--empty" aria-hidden />;
    return (
      <button
        type="button"
        className="kifu-cell"
        data-side={slot.e.side}
        data-active={slot.ply === currentPly}
        title={slot.consumed != null ? formatElapsed(slot.consumed) : undefined}
        onClick={() => onJump(slot.ply)}
      >
        <span className="kifu-mv">{describeMove(entry.game_id, slot.e.mv, slot.e.side)}</span>
        {slot.consumed != null && (
          <span className="kifu-time">{formatElapsed(slot.consumed)}</span>
        )}
      </button>
    );
  };
  return (
    <div className="kifu-table">
      <div className="kifu-head">
        <span className="kifu-num" />
        <span className="kifu-col-label" data-side="First">
          先手
        </span>
        <span className="kifu-col-label" data-side="Second">
          後手
        </span>
      </div>
      <ol className="kifu-list" ref={listRef}>
        {rows.map((row) => (
          <li key={row.num} className="kifu-row">
            <span className="kifu-num">{row.num}.</span>
            {renderCell(row.first)}
            {renderCell(row.second)}
          </li>
        ))}
      </ol>
    </div>
  );
}

type KifuSlot = { e: MoveLogEntry; ply: number; consumed: number | null } | null;
type KifuRow = { num: number; first: KifuSlot; second: KifuSlot };

/**
 * 手番ごとに 1 行 = (先手の手, 後手の手) のペアへまとめる。 チェス/将棋の標準棋譜と同じ
 * 「番号 + 二手」 形式。 First で必ず新しい行を開始し、 続く Second を右に埋める。
 * 連続同手番 (マンカラの追加手番、 Connect6 の 2 石など) は新しい行になり破綻しない。
 */
function pairPlies(history: MoveLogEntry[]): KifuRow[] {
  const rows: KifuRow[] = [];
  history.forEach((e, i) => {
    const consumed = i > 0 ? e.at_ms - history[i - 1].at_ms : null;
    const slot: KifuSlot = { e, ply: i + 1, consumed };
    if (e.side === 'First') {
      rows.push({ num: rows.length + 1, first: slot, second: null });
    } else {
      const last = rows[rows.length - 1];
      if (last && last.second == null) last.second = slot;
      else rows.push({ num: rows.length + 1, first: null, second: slot });
    }
  });
  return rows;
}

/**
 * 棋譜表記。 ゲームごとの慣例にできる範囲で寄せ、 1 手 = 短い 1 トークンにする。
 *   Reversi  : D5 / E6 / Pass
 *   Renju    : H8 / K11 / swap
 *   Chess    : e2-e4 / b1-c3 / e7-e8=Q / O-O / O-O-O / d5xc6 e.p.
 *   Shogi    : ▲7六歩 / △2二角 (from は省略; 「同〜」 は未対応)
 *   Jungo    : D5 / pass (9 路、 I 列スキップ)
 *   Animal   : B2-B3 / ひ@B2 (持ち駒打ち)
 *   Backgammon: 24/18 / bar/22 / 6/off / end / undo
 *   Janggi   : a1-b2 / 한수쉼 (9×10、 file a-i + rank 1-10)
 *   Xiangqi  : a1-a2 (9×10、 file a-i + rank 1-10)
 *   Connect6 : H8 / K11 (19 路、 I も使う、 rank = 19-row)
 *   Draughts : 33-28 / 27x18 (PDN 番号、 capture は x で経由を連結)
 *   Kalah    : 4番 / スワップ (自分側の手前から 1-6 番の穴)
 *   TwixT    : E8 / スワップ (24×24、 列 A-X + 行番号)
 *   Poker    : コール / ベット40 / ボード:T♣J♦Q♥ など
 */
export function describeMove(game: GameId, mv: unknown, side?: 'First' | 'Second'): ReactNode {
  if (game === 'Reversi') return describeReversi(mv);
  if (game === 'Renju') return describeRenju(mv);
  if (game === 'Chess') return describeChess(mv);
  if (game === 'Shogi') return describeShogi(mv, side);
  if (game === 'Jungo') return describeJungo(mv);
  if (game === 'AnimalShogi') return describeAnimal(mv);
  if (game === 'Backgammon') return describeBackgammon(mv);
  if (game === 'Janggi') return describeJanggi(mv);
  if (game === 'Xiangqi') return describeXiangqi(mv);
  if (game === 'Connect6') return describeConnect6(mv);
  if (game === 'Draughts') return describeDraughts(mv);
  if (game === 'Kalah') return describeKalah(mv, side);
  if (game === 'TwixT') return describeTwixt(mv);
  if (game === 'HeadsUpPoker') return describePoker(mv);
  if (game === 'BridgeNT' && typeof mv === 'object' && mv !== null && 'play' in mv) {
    const { card } = (mv as Extract<BridgeNtMove, { play: unknown }>).play;
    return <span className="bridge-replay-card" data-bridge-suit={card.suit} style={{ color: bridgeSuitColor(card.suit) }}>{suitLabel(card.suit)}{rankLabel(card.rank)}</span>;
  }
  return newGameMoveLabel(game, mv) ?? JSON.stringify(mv);
}

function describeJanggi(mv: unknown): string {
  if (mv === 'Pass') return '한수쉼';
  if (typeof mv === 'object' && mv !== null && 'Move' in mv) {
    const m = (mv as { Move: { from: [number, number]; to: [number, number] } }).Move;
    return `${janggiSquare(m.from)}-${janggiSquare(m.to)}`;
  }
  return JSON.stringify(mv);
}

function janggiSquare(fr: [number, number]): string {
  // (file, rank) = ('a'-'i', 1-10)
  return `${'abcdefghi'[fr[0]] ?? '?'}${fr[1] + 1}`;
}

// ─── Reversi / Jungo / Renju 共通: 列文字 + 段数 ───
function fileLetter(col: number, withI = true): string {
  // 囲碁慣例: I を飛ばす。 連珠/オセロは I も使う。
  const letters = withI ? 'ABCDEFGHIJKLMNOPQRS' : 'ABCDEFGHJKLMNOPQRS';
  return letters[col] ?? '?';
}

function describeReversi(mv: unknown): string {
  if (mv === 'Pass') return 'Pass';
  if (typeof mv === 'object' && mv && 'Place' in mv) {
    const p = (mv as { Place: { row: number; col: number } }).Place;
    // row=0 が上段 = rank 8 (オセロ慣例)。 表示は row=0 → 1 (画面上端から数える) でも
    // 直感的なので既存挙動踏襲。
    return `${fileLetter(p.col)}${p.row + 1}`;
  }
  return JSON.stringify(mv);
}

function describeRenju(mv: unknown): string {
  if (mv === 'Swap') return 'swap';
  if (typeof mv === 'object' && mv && 'Place' in mv) {
    const p = (mv as { Place: { row: number; col: number } }).Place;
    // RenjuBoard 規約: r=0 が画面上端、 rank = 15-r。 file = A.. (I も使う、 15 路)。
    return `${fileLetter(p.col)}${15 - p.row}`;
  }
  return JSON.stringify(mv);
}

function describeJungo(mv: unknown): string {
  if (mv === 'Pass') return 'pass';
  if (typeof mv === 'object' && mv && 'Place' in mv) {
    const p = (mv as { Place: { row: number; col: number } }).Place;
    // 囲碁慣例: I を飛ばす。 rank は下から (r=0 が画面上端 = rank 9)。
    return `${fileLetter(p.col, false)}${9 - p.row}`;
  }
  return JSON.stringify(mv);
}

function describeChess(mv: unknown): string {
  if (typeof mv !== 'object' || mv == null) return JSON.stringify(mv);
  if ('Castle' in mv) {
    const c = (mv as { Castle: { kingside: boolean } }).Castle;
    return c.kingside ? 'O-O' : 'O-O-O';
  }
  if ('Normal' in mv) {
    const n = (mv as { Normal: { from: [number, number]; to: [number, number] } }).Normal;
    return `${chessSquare(n.from)}-${chessSquare(n.to)}`;
  }
  if ('Promotion' in mv) {
    const p = (mv as {
      Promotion: { from: [number, number]; to: [number, number]; promote_to: string };
    }).Promotion;
    return `${chessSquare(p.from)}-${chessSquare(p.to)}=${chessPieceLetter(p.promote_to)}`;
  }
  if ('EnPassant' in mv) {
    const e = (mv as { EnPassant: { from: [number, number]; to: [number, number] } })
      .EnPassant;
    return `${chessSquare(e.from)}-${chessSquare(e.to)} e.p.`;
  }
  return JSON.stringify(mv);
}

function chessSquare(rc: [number, number]): string {
  // Chess engine: row=0 が白の最下段 = rank 1、 col=0 が a 列。
  return `${'abcdefgh'[rc[1]] ?? '?'}${rc[0] + 1}`;
}

function chessPieceLetter(p: string): string {
  switch (p) {
    case 'Queen':
      return 'Q';
    case 'Rook':
      return 'R';
    case 'Bishop':
      return 'B';
    case 'Knight':
      return 'N';
    default:
      return p[0] ?? '?';
  }
}

function describeShogi(mv: unknown, side?: 'First' | 'Second'): string {
  if (typeof mv !== 'object' || mv == null) return JSON.stringify(mv);
  const turnMark = side === 'First' ? '▲' : side === 'Second' ? '△' : '';
  if ('Push' in mv) {
    const p = (mv as {
      Push: { from: [number, number]; to: [number, number]; promote: boolean };
    }).Push;
    return `${turnMark}${shogiSquare(p.to)}${shogiPieceFromCoord()}${p.promote ? '成' : ''} (${shogiSquare(p.from)})`;
  }
  if ('Drop' in mv) {
    const d = (mv as { Drop: { kind: string; to: [number, number] } }).Drop;
    return `${turnMark}${shogiSquare(d.to)}${shogiPieceName(d.kind)}打`;
  }
  return JSON.stringify(mv);
}

function shogiSquare(rc: [number, number]): string {
  // Shogi engine: row=0 = 先手最下段 = 9段目、 col=0 = 9筋。 慣例表記 = 「筋(数)段(漢字)」。
  const file = 9 - rc[1]; // 1..9
  const rankK = '一二三四五六七八九'[9 - rc[0] - 1] ?? '?';
  return `${file}${rankK}`;
}

function shogiPieceFromCoord(): string {
  // 駒種別は move からは取れないため、 ここでは空 (= 棋譜の標準的な「同〜」 簡略形に近い)。
  // 将来 history + state を渡せるようになったら駒名を入れる。
  return '';
}

function shogiPieceName(piece: string): string {
  // engine の Drop piece は "Pawn" などの英語表記。 慣例和名へ。
  switch (piece) {
    case 'Pawn':
      return '歩';
    case 'Lance':
      return '香';
    case 'Knight':
      return '桂';
    case 'Silver':
      return '銀';
    case 'Gold':
      return '金';
    case 'Bishop':
      return '角';
    case 'Rook':
      return '飛';
    default:
      return piece;
  }
}

function describeAnimal(mv: unknown): string {
  if (typeof mv !== 'object' || mv == null) return JSON.stringify(mv);
  if ('Step' in mv) {
    const s = (mv as { Step: { from: [number, number]; to: [number, number] } }).Step;
    return `${animalSquare(s.from)}-${animalSquare(s.to)}`;
  }
  if ('Drop' in mv) {
    const d = (mv as { Drop: { piece: string; to: [number, number] } }).Drop;
    return `${animalPieceName(d.piece)}@${animalSquare(d.to)}`;
  }
  return JSON.stringify(mv);
}

function animalSquare(rc: [number, number]): string {
  // Animal-Shogi: 3 列 × 4 行。 col 0..2 → A..C、 row 0..3 → 1..4。
  return `${'ABC'[rc[1]] ?? '?'}${rc[0] + 1}`;
}

function animalPieceName(piece: string): string {
  switch (piece) {
    case 'Chick':
      return 'ひ';
    case 'Giraffe':
      return 'き';
    case 'Elephant':
      return 'ぞ';
    case 'Lion':
      return 'ラ';
    case 'Hen':
      return 'と';
    default:
      return piece;
  }
}

function describeBackgammon(mv: unknown): string {
  if (mv === 'end_turn') return 'end';
  if (mv === 'undo') return 'undo';
  if (typeof mv !== 'object' || mv == null) return JSON.stringify(mv);
  if ('step' in mv) {
    const s = (mv as { step: { from: number; dice: number } }).step;
    // to は from と dice から: First は 1 へ向かう (= from - dice)、 Second は 24 へ向かう (= from + dice)。
    // ただし side 情報なしでは方向特定できないので、 慣例の「from/to」 は from + dice の符号付きで省略し、
    // 「from→(dice)」 で代用する。 to を厳密に出すには side を渡す必要があるが、 既存 BG 棋譜の
    // 標準 (24/18 等) に合わせて「from/(from-dice)」 を出す (First 視点を仮定)。
    const to = s.from - s.dice;
    return `${s.from}/${to > 0 ? to : 0}`;
  }
  if ('enter' in mv) {
    const e = (mv as { enter: { dice: number } }).enter;
    // First の enter 先 = 25 - dice。 慣例表記「bar/<point>」。
    return `bar/${25 - e.dice}`;
  }
  if ('bear_off' in mv) {
    const b = (mv as { bear_off: { from: number } }).bear_off;
    return `${b.from}/off`;
  }
  return JSON.stringify(mv);
}

function describeXiangqi(mv: unknown): string {
  // XiangqiMove は struct { from:[file,rank], to:[file,rank] }。 file a-i / rank 1-10。
  if (typeof mv === 'object' && mv !== null && 'from' in mv && 'to' in mv) {
    const m = mv as { from: [number, number]; to: [number, number] };
    return `${janggiSquare(m.from)}-${janggiSquare(m.to)}`;
  }
  return JSON.stringify(mv);
}

function describeConnect6(mv: unknown): string {
  // Place{row,col}、 19 路。 連珠と同じく列文字 (I も使う) + 段 (rank=19-row)。
  if (typeof mv === 'object' && mv && 'Place' in mv) {
    const p = (mv as { Place: { row: number; col: number } }).Place;
    return `${fileLetter(p.col)}${19 - p.row}`;
  }
  return JSON.stringify(mv);
}

function describeDraughts(mv: unknown): string {
  // PDN 番号 (0-49 → 1-50)。 Step は from-to、 Capture は from x 経由 x ... (標準 PDN)。
  if (typeof mv === 'object' && mv && 'Step' in mv) {
    const s = (mv as { Step: { from: number; to: number } }).Step;
    return `${s.from + 1}-${s.to + 1}`;
  }
  if (typeof mv === 'object' && mv && 'Capture' in mv) {
    const c = (mv as { Capture: { from: number; path: number[] } }).Capture;
    return [c.from + 1, ...c.path.map((p) => p + 1)].join('x');
  }
  return JSON.stringify(mv);
}

function describeKalah(mv: unknown, side?: 'First' | 'Second'): string {
  // Sow{pit} (絶対 index)。 自分側の手前から 1-6 番に正規化。 First=0..5 / Second=7..12。
  if (mv === 'Swap') return 'スワップ';
  if (typeof mv === 'object' && mv && 'Sow' in mv) {
    const pit = (mv as { Sow: { pit: number } }).Sow.pit;
    const n = side === 'Second' ? pit - 6 : pit + 1; // どちらも 1..6
    return `${n}番`;
  }
  return JSON.stringify(mv);
}

function describeTwixt(mv: unknown): string {
  // Place{x,y}、 24×24。 列 A-X (x) + 行番号 (y+1)。
  if (mv === 'Swap') return 'スワップ';
  if (typeof mv === 'object' && mv && 'Place' in mv) {
    const p = (mv as { Place: { x: number; y: number } }).Place;
    const col = p.x >= 0 && p.x < 24 ? String.fromCharCode(65 + p.x) : '?';
    return `${col}${p.y + 1}`;
  }
  return JSON.stringify(mv);
}

const POKER_ACTION_LABEL: Record<string, string> = {
  fold: 'フォールド',
  check: 'チェック',
  call: 'コール',
  all_in: 'オールイン',
  next_hand: '次の手',
};

function describePoker(mv: unknown): ReactNode {
  if (typeof mv === 'string') return POKER_ACTION_LABEL[mv] ?? mv;
  if (typeof mv !== 'object' || mv == null) return JSON.stringify(mv);
  if ('bet' in mv) return `ベット${(mv as { bet: { amount: number } }).bet.amount}`;
  if ('raise' in mv) return `レイズ${(mv as { raise: { to_total: number } }).raise.to_total}`;
  if ('provide_my_hole' in mv) {
    const h = (mv as { provide_my_hole: { cards: { rank: number; suit: number }[] } })
      .provide_my_hole;
    if (h.cards.length === 0) return '手札を配布';
    return <>手札:{h.cards.map((card, i) => <PokerInlineCard key={i} card={card} />)}</>;
  }
  if ('provide_board_cards' in mv) {
    const b = (mv as { provide_board_cards: { cards: { rank: number; suit: number }[] } })
      .provide_board_cards;
    return <>ボード:{b.cards.map((card, i) => <PokerInlineCard key={i} card={card} />)}</>;
  }
  return JSON.stringify(mv);
}


function formatElapsed(ms: number): string {
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}秒`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}分${s}秒`;
}

export function reasonLabel(reason: string | null): string {
  const newReasons: Record<string, string> = {
    "abalone_six_off": "6玉押し出し",
    "abalone_no_moves": "合法手なし",
    "abalone_ply_cap": "手数上限・押し出し数判定",
    "gomoku_five": "五連",
    "gomoku_board_full": "盤満杯（引き分け）",
    "kyoen_concyclic": "4石が同じ円周上",
    "kyoen_collinear": "4石が一直線",
    "kyoen_no_move": "空点なし",
    "kyoen_invalid_claim": "誤宣言",
    "bridge_nt_dd_diff": "NT・最善配牌基準との差分",
    "trax_loop": "ループ完成",
    "trax_line": "8列または8行の線完成",
    "trax_max_plies": "手数上限（引き分け）",
    "amazons_no_moves": "移動可能な駒なし",
    "amazons_territory": "領域分離による確定",
    "chu_shogi_royal_captured": "王族捕獲",
    "chu_shogi_checkmate": "詰み",
    "chu_shogi_stalemate": "合法手なし",
    "chu_shogi_bare_king": "裸王",
    "chu_shogi_sennichite": "千日手",
    "chu_shogi_bare_kings": "双方裸王（引き分け）",
    "chu_shogi_max_plies": "手数上限（引き分け）"
};
  if (reason && newReasons[reason]) return newReasons[reason];
  switch (reason) {
    // generic
    case 'resign':
      return '投了';
    case 'draw_agreed':
      return 'ドロー合意';
    case 'timeout':
      return '時間切れ';
    case 'win_by_score':
      return 'スコア決着';
    // Renju
    case 'renju_five':
      return '五連';
    case 'renju_forbidden':
      return '黒禁手 (三三・四四)';
    case 'renju_overline':
      return '長連 (黒禁手)';
    // Chess
    case 'chess_checkmate':
      return 'チェックメイト';
    case 'chess_stalemate':
      return 'ステイルメイト (引き分け)';
    case 'chess_fifty_move':
      return '50 手ルール';
    case 'chess_seventyfive_move':
      return '75 手ルール';
    case 'chess_threefold':
      return '3 回繰り返し';
    case 'chess_fivefold':
      return '5 回繰り返し';
    case 'chess_insufficient_material':
      return '駒不足';
    // Shogi
    case 'shogi_checkmate':
      return '詰み';
    case 'shogi_try':
      return 'トライ勝ち';
    case 'shogi_sennichite':
      return '千日手';
    case 'shogi_perpetual_check':
      return '連続王手の千日手 (反則)';
    // Jungo
    case 'jungo_pass_alive':
      return '上がり (パス活性)';
    case 'jungo_two_passes':
      return '双方パス';
    // AnimalShogi
    case 'animal_catch':
      return 'ライオン捕獲';
    case 'animal_try':
      return 'トライ勝ち';
    case 'animal_stalemate':
      return 'ステイルメイト';
    case 'animal_repetition':
      return '千日手 (引き分け)';
    case 'animal_max_plies':
      return '手数超過 (引き分け)';
    // Reversi
    case 'reversi_draw':
      return '引き分け';
    case 'reversi_win_by_score':
      return '石数勝ち';
    // Backgammon
    case 'bg_bear_off':
      return 'ベアオフ勝ち';
    // Xiangqi
    case 'xiangqi_checkmate':
      return '詰み (将軍)';
    case 'xiangqi_stalemate':
      return 'ステイルメイト (動けず負け)';
    case 'xiangqi_perpetual_check':
      return '長将 (連続王手反則)';
    case 'xiangqi_sixty_move':
      return '60 手進歩なし (引き分け)';
    case 'xiangqi_threefold':
      return '同形 3 回反復 (引き分け)';
    case 'xiangqi_mutual_perpetual_check':
      return '双方長将 (引き分け)';
    // Janggi
    case 'janggi_checkmate':
      return '외통 (詰み)';
    case 'janggi_bikjang':
      return '빅장 (両王対峙、 점수決着)';
    case 'janggi_repetition':
      return '同形 3 回 (점수決着)';
    case 'janggi_two_passes':
      return '双方 한수쉼 (점수決着)';
    case 'janggi_ply_cap':
      return '手数超過 (점수決着)';
    case 'disconnect':
      return '切断';
    // Heads-Up Poker
    case 'poker_bust_out':
      return 'バストアウト勝ち';
    default:
      return '';
  }
}


function extractMatchId(archiveKey: string): string | null {
  // archive_key = "matches/<match_id>/game.sgf"
  const parts = archiveKey.split('/');
  return parts[1] ?? null;
}

/** ローカル対局 ID を発番 (CpuSetup.generateLocalId と同じ形式: 8 文字)。 */
function generateLocalId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}
